import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { AccountTeamMember } from '@/types/accountTeam';

export type AccountStatus =
  | 'Active'
  | 'NonRenewing'
  | 'Cancelled'
  | 'Trial'
  | 'Paused'
  | 'Future'
  | 'Onboarding'
  | 'Customer'
  | 'Prospect'
  | 'Lead'
  | 'Inactive';

export type DealDisposition = 'Won' | 'Lost' | 'Open';

export interface LatestDealOutcome {
  disposition: DealDisposition;
  date: string;
}

export interface AccountSummary {
  id: string;
  billingId: string | null;
  name: string;
  hubspotName: string | null;
  hasNameDrift: boolean;
  hubspotCompanyId: string | null;
  status: AccountStatus;
  ownerEmail: string | null;
  ownerName: string | null;
  industry: string | null;
  lastSyncedAt: string | null;
  lastActivityAt: string | null;
  mrr: number;
  activeSubscriptionCount: number;
  nextRenewalAt: string | null;
  productNames: string[];
  latestDealOutcome?: LatestDealOutcome | null;
  teamMembers?: AccountTeamMember[] | null;
}

export interface AccountListResponse {
  accounts: AccountSummary[];
  totalCount: number;
}

export interface PagedAccountListResponse {
  items: AccountSummary[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface AccountListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
  statuses?: string;
  ownerEmails?: string;
  cancellationReasons?: string;
  cancelledWithinDays?: number;
  latestDealOutcomes?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

interface UseAccountsPagedOptions {
  enabled?: boolean;
}

export const ACCOUNTS_QUERY_KEY = 'accounts';

export function useAccounts() {
  return useQuery({
    queryKey: [ACCOUNTS_QUERY_KEY, 'all'],
    queryFn: async () => {
      const { data } = await api.get<AccountListResponse>('/accounts/all');
      return data;
    },
  });
}

export interface AccountOwner {
  email: string;
  name: string;
  accountCount: number;
}

export interface AccountOwnerListResponse {
  owners: AccountOwner[];
}

export function useAccountOwners(enabled: boolean) {
  return useQuery({
    queryKey: [ACCOUNTS_QUERY_KEY, 'owners'],
    queryFn: async () => {
      const { data } = await api.get<AccountOwnerListResponse>('/accounts/owners');
      return data;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAccountsPaged(
  params: AccountListParams = {},
  options: UseAccountsPagedOptions = {},
) {
  return useQuery({
    queryKey: [ACCOUNTS_QUERY_KEY, 'paged', params],
    queryFn: async () => {
      const { data } = await api.get<PagedAccountListResponse>('/accounts', { params });
      return data;
    },
    placeholderData: keepPreviousData,
    enabled: options.enabled ?? true,
  });
}
